import React, { useEffect, useState } from 'react';
import { wishlistAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { Loader, Trash2, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

const Wishlist = () => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const [removingIndex, setRemovingIndex] = useState(null);

    useEffect(() => {
        fetchWishlist();
    }, []);

    const fetchWishlist = async () => {
        try {
            const response = await wishlistAPI.getAll();
            console.log('Wishlist API response:', response.data);
            // Backend returns { items: [...], totalItems, currentPage, totalPages }
            setWishlistItems(response.data.items || response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch wishlist", error);
        } finally {
            setLoading(false);
        }
    };

    const removeFromWishlist = async (variationId, index) => {
        setRemovingIndex(index);
        try {
            await wishlistAPI.remove(variationId);
            setWishlistItems(prev => prev.filter((_, i) => i !== index));
        } catch (error) {
            console.error("Failed to remove from wishlist", error);
            alert("Failed to remove item.");
        } finally {
            setRemovingIndex(null);
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12">
            <div className="container mx-auto max-w-6xl">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">My Wishlist</h1>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="animate-spin text-green-600" size={48} />
                    </div>
                ) : wishlistItems.length === 0 ? (
                    <div className="bg-white rounded-xl shadow p-12 text-center">
                        <ShoppingBag className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                        <h2 className="text-xl font-medium text-gray-900 mb-2">Your wishlist is empty</h2>
                        <p className="text-gray-500 mb-6">Explore our products and save your favorites for later.</p>
                        <Link to="/products" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700">
                            Start Shopping
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {wishlistItems.map((item, index) => {
                            // Wishlist item structure: { id, user_id, product_variation_id, variation: { id, price, image, product: {...} } }
                            const variation = item.variation || {};
                            const product = variation.product || item.product || item;
                            let imageUrl = 'https://via.placeholder.com/300';

                            // Handle image from variation
                            if (variation.image) {
                                let images = variation.image;
                                if (typeof images === 'string') {
                                    try { images = JSON.parse(images); } catch (e) { images = [images]; }
                                }
                                const firstImage = Array.isArray(images) ? images[0] : images;
                                imageUrl = getImgUrl(firstImage);
                            }

                            return (
                                <div key={item.id || index} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={imageUrl}
                                            alt={product.name}
                                            className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-500"
                                        />
                                        <button
                                            onClick={() => removeFromWishlist(item.product_variation_id, index)}
                                            className="absolute top-2 right-2 bg-white/80 p-2 rounded-full text-red-500 hover:bg-white hover:text-red-600 transition-colors shadow-sm"
                                            title="Remove from wishlist"
                                        >
                                            {removingIndex === index ? (
                                                <Loader className="animate-spin h-5 w-5" />
                                            ) : (
                                                <Trash2 size={20} />
                                            )}
                                        </button>
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-1 line-clamp-1">{product.name}</h3>
                                        {/* Display price from variation */}
                                        {variation.price && (
                                            <p className="text-green-600 font-bold mb-3">₹{variation.price}</p>
                                        )}

                                        <div className="mt-auto">
                                            <Link
                                                to="/products" // Ideally link to product detail if it existed
                                                className="w-full block text-center bg-green-50 text-green-700 py-2 rounded-lg hover:bg-green-100 transition-colors font-medium"
                                            >
                                                View Product
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wishlist;
